import type { AxiosInstance } from 'axios';
import { useEffect, useState } from 'react';

import { useMaskingUserType } from '@/hooks/useMaskingUserType';
import type { SimpleFilterDefinition } from '@/sections/insights/queryBuilder/entities/types';
import { useQueryUI } from '@/sections/insights/queryBuilder/state/QueryUIContext';
import { filterInternalTags } from '@/utils/userDisplay.util';

/**
 * Custom hook for fetching filter values with loading state management.
 *
 * Lazy-loads values from filterDef.getAvailableValues() when the hook is first called.
 * Handles loading state, error state, and race condition cleanup.
 *
 * @param filterDef - Filter definition containing optional getAvailableValues function
 * @param axios - Authenticated axios instance for API calls
 * @returns Object with options array, loading boolean, and error string
 *
 * @example
 * const axios = useAxios();
 * const { options, isLoading, error } = useFilterValues(fieldDef, axios);
 */
export function useFilterValues(
  filterDef: SimpleFilterDefinition | undefined,
  axios: AxiosInstance
): {
  options: string[];
  isLoading: boolean;
  error: string | null;
} {
  const { isGlobal } = useQueryUI();
  const maskingUserType = useMaskingUserType();
  const [options, setOptions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset state when field changes (fixes dropdown persistence bug)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOptions([]);
    setError(null);

    const shouldLoad = filterDef?.getAvailableValues; // Function exists

    if (!shouldLoad) {
      return;
    }

    let cancelled = false;

    const fetchValues = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const values = await filterDef.getAvailableValues!(axios, { isGlobal });
        // Hide internal hunt/agent/cato provenance tags from customers
        // (Praetorian sees all). Per-definition opt-in via maskInternalTags —
        // set on RiskTags only, since those tags are written exclusively to
        // Risk.Tags; other entities' tag values (e.g. AssetTags) are
        // customer-created and stay unmasked.
        const visibleValues = filterDef.maskInternalTags
          ? filterInternalTags(values, maskingUserType)
          : values;
        if (!cancelled) {
          setOptions(visibleValues);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load field values:', err);
          setError('Failed to load options');
          setIsLoading(false);
        }
      }
    };

    fetchValues();

    return () => {
      cancelled = true;
    };
  }, [
    filterDef?.apiField,
    filterDef?.maskInternalTags,
    axios,
    isGlobal,
    maskingUserType,
  ]);

  return { options, isLoading, error };
}
